/**
 * Quality / Tests and coverage / a run (GET /quality/runs/:runId): who started it and why, what changed since the
 * previous run (newly failing, fixed, new and removed tests, the coverage delta), its tests (QualityTestTable), the
 * coverage at that run and each service's logs. A run from before v3, or without the tests check, has its summary only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getRunDetail } from '../../services/QualityService';
import { Pill, State, SubHead } from './QualityCommon';
import QualityTestTable from './QualityTestTable';
import { errorText, formatDuration, pct, plural, runLabel, testStatusOf } from './qualityModel';

const TRIGGER_WORDS = { manual: 'Started by hand', publish: 'Before Publish', change: 'After a change' };

function RefList({ title, refs, tone, testId }) {
  if (!refs?.length) { return null; }
  return (
    <div className="quality-run-diff-group" data-testid={testId}>
      <h3 className="quality-small">{title} <Pill className="is-xs" tone={tone}>{refs.length}</Pill></h3>
      <ul className="quality-lines">
        {refs.map((r) => <li key={r.id}><span className="quality-mono">{r.service}{r.file ? ` › ${r.file}` : ''}</span> · {[r.suite, r.name].filter(Boolean).join(' › ')}{r.status ? ` (${testStatusOf(r.status).label.toLowerCase()})` : ''}</li>)}
      </ul>
    </div>
  );
}

RefList.propTypes = { refs: PropTypes.array, testId: PropTypes.string, title: PropTypes.string.isRequired, tone: PropTypes.string.isRequired };

/** What changed by counts (a service with no named test on either side): "1 fixed, 1 newly failing, 2 more tests". */
function countChanges(byCount) {
  if (!byCount) { return []; }
  const parts = [];
  if (byCount.fixed) { parts.push(`${byCount.fixed} fixed`); }
  if (byCount.newlyFailing) { parts.push(`${byCount.newlyFailing} newly failing`); }
  if (byCount.added) { parts.push(plural(byCount.added, 'more test')); }
  if (byCount.removed) { parts.push(`${plural(byCount.removed, 'test')} fewer`); }
  return parts;
}

const countsText = (c) => `${c.failed} failed of ${plural(c.total, 'test')}`;

const delta = (d) => (d === null || d === undefined ? '' : ` (${d > 0 ? '+' : ''}${Math.round(d * 10) / 10} points)`);

export default function QualityRunDetail({ projectId, runId, onBack, canEdit = false, onAskAI = null, onOpenCode = null, onOpenRun = null }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setError(null);
    try { setDetail(await getRunDetail(projectId, runId)); } catch (e) { setError(e); }
  }, [projectId, runId]);
  useEffect(() => { load(); }, [load]);

  const run = detail?.run || null;
  const title = run ? runLabel(run) : 'Run';
  const diff = detail?.diff || null;
  const cov = diff?.coverage || null;
  // Services without per-test detail on either side are compared by their counts (vfs testDetail.js diffRuns).
  const byCount = diff?.counts && diff.counts.perTest === false ? diff.counts : null;
  const counted = countChanges(byCount?.byCount);
  const nothingChanged = diff && !diff.newlyFailing?.length && !diff.fixed?.length && !diff.added?.length && !diff.removed?.length && !cov?.delta && !counted.length;

  return (
    <div className="quality-sub" data-testid="quality-run-detail">
      <SubHead hint={run?.runId || runId} onBack={onBack} title={title} />
      {error && !detail && (
        error.status === 404
          ? <State title="This run is not kept any more">Flow keeps the details of the last 20 runs that ran tests.</State>
          : <State action={<button className="quality-btn" onClick={load} type="button">Try again</button>} title="Could not load the run" tone="bad">{errorText(error)}</State>
      )}
      {!error && !detail && <div className="quality-state" role="status">Loading the run…</div>}
      {run && (
        <section aria-label="Run" className="quality-card quality-pad quality-small" data-testid="quality-run-summary">
          <span>{TRIGGER_WORDS[run.trigger] || run.trigger}{run.by?.name ? ` · ${run.by.name}` : ''} · took {formatDuration(run.durationMs)}{run.state && run.state !== 'done' ? ` · ${run.state}` : ''}</span>
          <span className="quality-muted">Run id <code className="quality-mono">{run.runId}</code>{detail.previous ? <> · previous: {onOpenRun ? <button className="quality-link" onClick={() => onOpenRun(detail.previous.runId)} title={detail.previous.runId} type="button">{detail.previous.label ? runLabel({ runId: detail.previous.runId, label: detail.previous.label }) : detail.previous.runId}</button> : detail.previous.runId}</> : ''}</span>
        </section>
      )}
      {detail && detail.detailAvailable === false && <State title="Only the summary of this run">It ran before Flow kept every test, or without the tests check.</State>}

      {diff && (
        <section aria-labelledby="quality-run-diff-title" className="quality-card quality-pad" data-testid="quality-run-diff">
          <h2 id="quality-run-diff-title">Since the previous run</h2>
          {nothingChanged && !byCount && <p className="quality-muted quality-small">Nothing changed: the same tests, the same results.</p>}
          {byCount && (
            <div className="quality-run-diff-group" data-testid="quality-diff-counts">
              <p className="quality-small">{counted.length ? counted.join(', ') : 'The same counts as the previous run'}</p>
              <p className="quality-muted quality-small">
                Compared by counts: {byCount.services.map((c) => c.service).join(', ')} had no named tests in one of the two runs, so the tests cannot be told apart.
                {byCount.services.map((c) => <span key={c.service}> {c.service}: {countsText(c.before)} → {countsText(c.after)}.</span>)}
              </p>
            </div>
          )}
          <RefList refs={diff.newlyFailing} testId="quality-diff-failing" title="Newly failing" tone="fail" />
          <RefList refs={diff.fixed} testId="quality-diff-fixed" title="Fixed" tone="pass" />
          <RefList refs={diff.added} testId="quality-diff-added" title="New tests" tone="info" />
          <RefList refs={diff.removed} testId="quality-diff-removed" title="Removed tests" tone="info" />
          {cov && (cov.before !== null || cov.after !== null) && (
            <p className="quality-small" data-testid="quality-diff-coverage">
              Line coverage {pct(cov.before)} → {pct(cov.after)}{delta(cov.delta)}
              {(cov.services || []).filter((s) => s.delta).map((s) => <span className="quality-muted" key={s.service}> · {s.service} {pct(s.before)} → {pct(s.after)}</span>)}
            </p>
          )}
        </section>
      )}

      {detail?.tests?.services?.length > 0 && (
        <section aria-labelledby="quality-run-tests-title" className="quality-card quality-pad">
          <h2 id="quality-run-tests-title">Tests</h2>
          <p className="quality-muted quality-small">
            {detail.tests.services.map((s) => `${s.service}: ${plural(s.total || 0, 'test')}, ${s.failed || 0} failed${s.skipped ? `, ${s.skipped} skipped` : ''}`).join(' · ')}
          </p>
          <QualityTestTable canEdit={canEdit} onAskAI={onAskAI} onOpenCode={onOpenCode} services={detail.tests.services} />
        </section>
      )}

      {detail?.coverage && (
        <section aria-labelledby="quality-run-cov-title" className="quality-card quality-pad" data-testid="quality-run-coverage">
          <h2 id="quality-run-cov-title">Coverage at this run</h2>
          <p className="quality-small">Lines {detail.coverage.lines === null || detail.coverage.lines === undefined ? 'not measured' : pct(detail.coverage.lines)}</p>
          <ul className="quality-lines">
            {(detail.coverage.services || []).map((s) => (
              <li key={s.service}><span className="quality-mono">{s.service}</span>: {s.totals ? `lines ${pct(s.totals.lines)}, branches ${pct(s.totals.branches)}, functions ${pct(s.totals.functions)}` : (s.coverageNote || 'Coverage not measured')}</li>
            ))}
          </ul>
        </section>
      )}

      {detail?.logs?.length > 0 && (
        <section aria-labelledby="quality-run-logs-title" className="quality-card quality-pad">
          <h2 id="quality-run-logs-title">Logs</h2>
          {detail.logs.map((log) => (
            <details className="quality-run-log" data-testid={`quality-run-log-${log.service}`} key={log.service}>
              <summary><span className="quality-mono">{log.service}</span> · <code className="quality-mono">{log.command || '–'}</code> · exit {log.exitCode ?? '–'}</summary>
              {log.stdout && <pre aria-label={`${log.service} stdout`} className="quality-pre quality-mono">{log.stdout}</pre>}
              {log.stderr && <pre aria-label={`${log.service} stderr`} className="quality-pre quality-mono">{log.stderr}</pre>}
              {!log.stdout && !log.stderr && <p className="quality-muted quality-small">No output.</p>}
            </details>
          ))}
        </section>
      )}
    </div>
  );
}

QualityRunDetail.propTypes = {
  canEdit: PropTypes.bool,
  onAskAI: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  onOpenCode: PropTypes.func,
  // (runId) => void: another run (the previous one).
  onOpenRun: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  runId: PropTypes.string.isRequired,
};
